import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { authAPI } from '../api';

export type ScopeLevel = 'global' | 'org' | 'branch';

export interface ScopedBranch {
  id: number;
  name: string;
}

interface User {
  id: number;
  email: string;
  name: string;
  employee_id?: number;
  org_id?: number;
  /** Primary/default branch. NOT the access authority — use branch_ids for that. */
  branch_id?: number;
  scope_level?: ScopeLevel;
  /** null = no branch restriction (org- or global-scoped). undefined = old backend. */
  branch_ids?: number[] | null;
  branches?: ScopedBranch[];
  is_active?: boolean;
  roles: string[];
  permissions: string[];
  can: Record<string, boolean>;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  isAuthenticated: boolean;
  can: (permission: string) => boolean;
  hasRole: (role: string) => boolean;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(localStorage.getItem('auth_token'));
  const [loading, setLoading] = useState(true);

  const refreshUser = useCallback(async () => {
    if (!token) {
      setUser(null);
      return;
    }

    try {
      const response = await authAPI.me();
      setUser(response.user);
    } catch (error) {
      localStorage.removeItem('auth_token');
      setToken(null);
      setUser(null);
    }
  }, [token]);

  // Initialize auth from token
  useEffect(() => {
    const initAuth = async () => {
      if (token) {
        await refreshUser();
      } else {
        setUser(null);
      }
      setLoading(false);
    };

    initAuth();
  }, [token, refreshUser]);

  const login = async (email: string, password: string) => {
    const response = await authAPI.login(email, password);
    // response is already data (axios interceptor extracts it)
    const { token: newToken, user: userData } = response;

    localStorage.setItem('auth_token', newToken);
    setToken(newToken);
    setUser(userData);
  };

  const logout = async () => {
    try {
      await authAPI.logout();
    } finally {
      localStorage.removeItem('auth_token');
      setToken(null);
      setUser(null);
    }
  };

  const can = (permission: string): boolean => {
    if (!user) return false;
    // Check user.can object first (precomputed on backend)
    if (user.can && typeof user.can[permission] === 'boolean') {
      return user.can[permission];
    }
    // Fallback to permissions array
    return user.permissions?.includes(permission) || false;
  };

  const hasRole = (role: string): boolean => {
    if (!user) return false;
    return user.roles?.includes(role) || false;
  };

  const value: AuthContextType = {
    user,
    token,
    loading,
    login,
    logout,
    isAuthenticated: !!user,
    can,
    hasRole,
    refreshUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
